import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Lock, Check, Eye, EyeOff } from 'lucide-react';
import { resetPassword } from '../lib/api';

/**
 * `/reset-password?token=...` — the second half of the password reset flow.
 *
 * Reads the single-use token from the query string, POSTs to
 * `/api/auth/reset-password` with the new password. On success:
 *   • the backend updates the user's password_hash
 *   • bumps `sessions_invalidated_at` so every existing JWT for that
 *     account is rejected (logout-everywhere)
 *   • the token row is deleted (single-use)
 *
 * We deliberately do NOT auto-sign the user in — they've just been
 * signed out everywhere, so surfacing an explicit "sign in with your
 * new password" screen is the least-surprising UX.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const validate = () => {
    if (!token) return 'This reset link is missing its token. Request a fresh one.';
    if (password.length < 6) return 'Password must be at least 6 characters.';
    if (password.length > 128) return 'Password is too long.';
    if (password !== confirm) return 'The two passwords don\'t match.';
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    const problem = validate();
    if (problem) { setErr(problem); return; }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : (e2.message || 'Could not reset your password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-10" data-testid="reset-password-page">
      <div className="flex items-center gap-2 mb-3">
        <Lock size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="reset-password-title">
          Choose a new password
        </h1>
      </div>

      {!token && !done ? (
        <div
          className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
          data-testid="reset-password-missing-token"
        >
          <div className="text-sm">
            This link doesn&apos;t look complete. <Link to="/forgot-password" className="underline font-medium">Request a new one</Link>.
          </div>
        </div>
      ) : done ? (
        <div
          className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200"
          data-testid="reset-password-success"
        >
          <div className="flex items-start gap-3">
            <Check size={20} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-1">Password updated.</div>
              <div className="text-sm mb-3">
                You&apos;ve been signed out of every device for safety.
                Sign back in with your new password.
              </div>
              <button
                onClick={() => navigate('/')}
                data-testid="reset-password-go-to-signin-btn"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-zinc-500 mb-6">
            For your security, signing in with the new password will end every existing session on any device.
          </p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="rp-new" className="block text-sm font-medium mb-1">New password</label>
              <div className="relative">
                <input
                  id="rp-new"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-new-input"
                  minLength={6}
                  maxLength={128}
                  className="w-full p-3 pr-10 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  data-testid="reset-password-toggle-visibility-btn"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="text-xs text-zinc-400 mt-1">At least 6 characters.</div>
            </div>
            <div>
              <label htmlFor="rp-confirm" className="block text-sm font-medium mb-1">Confirm new password</label>
              <input
                id="rp-confirm"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm-input"
                minLength={6}
                maxLength={128}
                className="w-full p-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            {err && (
              <div
                className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm"
                data-testid="reset-password-error"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || !password || !confirm}
              data-testid="reset-password-submit-btn"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold w-full sm:w-auto"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              Update password
            </button>
          </form>
          <div className="mt-6 text-xs text-zinc-500">
            Changed your mind? <Link to="/" className="text-amber-600 hover:underline">Back to sign in</Link>.
          </div>
        </>
      )}
    </div>
  );
};

export default ResetPassword;
